import { createContext, useContext, ReactNode, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChallengeRepository } from '@/repositories/ChallengeRepository'
import { CategoryRepository } from '@/repositories/CategoryRepository'
import { Challenge, ChallengeCategory } from '@/types/challenge'
import { useTranslation } from '@/i18n/hooks/useTranslation'

interface ChallengeContextValue {
  challenges: Challenge[]
  categories: ChallengeCategory[]
  isLoading: boolean
  isError: boolean
  error: Error | null
  challengeRepository: ChallengeRepository
  categoryRepository: CategoryRepository
  getChallenge: (id: string) => Challenge | undefined
  getChallengesByCategory: (categoryId: string) => Challenge[]
  refetch: () => void
}

const ChallengeContext = createContext<ChallengeContextValue | undefined>(undefined)

interface ChallengeProviderProps {
  children: ReactNode
}

export function ChallengeProvider({ children }: ChallengeProviderProps) {
  const { currentLanguage } = useTranslation()
  const [challengeRepository] = useState(() => new ChallengeRepository())
  const [categoryRepository] = useState(() => new CategoryRepository())

  // Batch fetch challenges and categories with language fallback
  const {
    data: challengeData = { challenges: [], categories: [] },
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['challengeData', currentLanguage],
    queryFn: async () => {
      
      // Batch both queries with fallback logic
      const fetchWithFallback = async (lang: string) => {
        const [challenges, categoryChallenges] = await Promise.all([
          challengeRepository.getChallenges(lang),
          categoryRepository.getCategories(lang)
        ])
        
        // Convert CategoryChallenge to ChallengeCategory
        const categories: ChallengeCategory[] = categoryChallenges.map(category => ({
          id: category.id,
          name: category.name,
          description: category.description,
          icon: category.icon,
          language: category.language,
          status: category.status as 'active' | 'archived',
          order: category.order,
          createdAt: category.createdAt.toISOString(),
          updatedAt: category.updatedAt.toISOString()
        }))
        
        return { challenges, categories }
      }
      
      // Try the requested language first
      let result = await fetchWithFallback(currentLanguage)
      
      // If no data and not English, try English as fallback
      if ((result.challenges.length === 0 || result.categories.length === 0) && currentLanguage !== 'en') {
        console.log(`🔄 Falling back to English for missing data. Challenges: ${result.challenges.length}, Categories: ${result.categories.length}`)
        result = await fetchWithFallback('en')
      }
      
      
      return result
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
  })

  // Extract data from batched query
  const { challenges: finalChallenges, categories: finalCategories } = challengeData

  // Log errors clearly
  useEffect(() => {
    if (isError) {
      console.error('❌ Challenge data failed to load:', error)
    }
  }, [isError, error, isLoading])

  const getChallenge = (id: string): Challenge | undefined => {
    return finalChallenges.find((challenge: Challenge) => challenge.id === id)
  }

  const getChallengesByCategory = (categoryId: string): Challenge[] => {
    return finalChallenges.filter((challenge: Challenge) => challenge.category === categoryId)
  }

  // refetch function already available from the single query

  const value: ChallengeContextValue = {
    challenges: finalChallenges,
    categories: finalCategories,
    challengeRepository,
    categoryRepository,
    isLoading,
    isError,
    error,
    getChallenge,
    getChallengesByCategory,
    refetch,
  }

  return (
    <ChallengeContext.Provider value={value}>
      {children}
    </ChallengeContext.Provider>
  )
}

export function useChallenges() {
  const context = useContext(ChallengeContext)
  if (context === undefined) {
    throw new Error('useChallenges must be used within a ChallengeProvider')
  }
  return context
} 